/**
 * Which clinic sessions are still open for booking on a given date (#19041).
 *
 * <p><b>The bug these rules close.</b> At 17:26 the booking form still offered "Chiều 13:30–17:00"
 * of that same day, still had "Sáng 07:30–11:30" preselected, and saved happily — the appointment
 * carrying `startAt = 13:30`, nearly four hours in the past. It then entered today's queue, was
 * pushed to the hospital over MQ, and consumed that session's capacity.
 *
 * <p>Kept as pure functions of an explicit `now` so the tests mean the same thing at 03:00 as at
 * 23:00. Everything compares `"HH:mm"` strings, which is the same ordering as comparing times
 * because the API zero-pads.
 */

export interface SessionWindow {
  /** `"HH:mm"` or `"HH:mm:ss"` — the API sends seconds, the UI shows minutes. */
  startTime: string;
  endTime: string;
}

/** `"HH:mm:ss"` → `"HH:mm"`; already-short values pass through. */
export function hhmm(time: string): string {
  return time.slice(0, 5);
}

/** Local calendar date of `now` as `YYYY-MM-DD`, matching the form's date input. */
export function localIsoDate(now: Date): string {
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, "0");
  const d = String(now.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

/**
 * Has this session already finished on `dateIso`?
 *
 * <p>Only today can have a finished session: a past DATE is refused by the advance-window rule
 * that already exists, and a future date has not started. The boundary is **closed** — at exactly
 * 17:00 the 13:30–17:00 session has not ended, so that minute is still bookable. Getting that
 * backwards refuses one legitimate minute per session per day, which is precisely the kind of
 * small wrongness nobody reports and everybody works around.
 */
export function hasEnded(session: SessionWindow, dateIso: string, now: Date): boolean {
  if (dateIso !== localIsoDate(now)) return false;
  const nowHhmm = hhmm(
    `${String(now.getHours()).padStart(2, "0")}:${String(now.getMinutes()).padStart(2, "0")}`
  );
  return nowHhmm > hhmm(session.endTime);
}

/**
 * The start time of the first session on `dateIso` that has not ended, or `null` when the day has
 * none left.
 *
 * <p>This is what the form preselects. Preselecting the first session unconditionally is how a
 * booking made at 17:26 defaulted to a morning that ended at 11:30 — the desk had to notice and
 * change it, and nothing on screen said so.
 */
export function firstOpenSessionStart(
  sessions: SessionWindow[],
  dateIso: string,
  now: Date
): string | null {
  const open = sessions.find((s) => !hasEnded(s, dateIso, now));
  return open ? hhmm(open.startTime) : null;
}
